import { useSelector } from "react-redux";

import { isNoteCompleted } from "@/features/notes/(v2)/helpers/isNoteCompleted";
import type { NoteDetail } from "@/core/types";
import { selectProfile } from "@/redux/selectors/authSelectors";
import { useScrollToAnchor } from "@/lib/hooks/useScrollToAnchor";

import { AddNoteAddendum } from "./components/AddNoteAddendum";
import { NoteAddendum } from "./components/NoteAddendum";
import { useNoteAddendums } from "./api/queries/useNoteAddendums";

type AddendumsSectionProps = {
  note: NoteDetail;
};

export function AddendumList({ note }: AddendumsSectionProps) {
  const { id: noteId, patient_id: patientId } = note;

  const profile = useSelector(selectProfile);
  const { data: addendums = [], isSuccess } = useNoteAddendums({ noteId, patientId });

  useScrollToAnchor(isSuccess);

  const isCurrentUserNoteAuthor = note.created_by === profile?.username;
  const hasSignature = Boolean(note.signature);
  const hasMaximumAddendums = addendums?.length >= MAX_ADDENDUMS;
  const isNoteComplete = isNoteCompleted(note.note_state);
  const isAdvancedCareActivityNoteType = note.note_type === "Advanced Care Activity";
  const hasCompleteAddendums = addendums.every(
    (addendum) => addendum.addendum_state === "complete",
  );

  const canAddNewAddendum =
    isSuccess &&
    isCurrentUserNoteAuthor &&
    hasSignature &&
    isNoteComplete &&
    hasCompleteAddendums &&
    !isAdvancedCareActivityNoteType &&
    !hasMaximumAddendums;

  return (
    <>
      {isNoteComplete &&
        addendums?.map((addendum, index) => (
          <NoteAddendum
            addendum={addendum}
            credentials={note.provider_cp_credentials}
            isAuthor={isCurrentUserNoteAuthor}
            key={addendum.addendum_id ?? index}
            noteId={noteId}
            patientId={patientId}
            signature={note.signature}
            textTestId={`note-addendum-text-${index}`}
          />
        ))}

      {canAddNewAddendum && (
        <AddNoteAddendum
          credentials={note.provider_cp_credentials}
          noteId={noteId}
          patientId={patientId}
          signature={note.signature}
        />
      )}
    </>
  );
}

const MAX_ADDENDUMS = 5;
